import { Add, ArrowBack, Delete, Edit, Save } from "@mui/icons-material"
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import {
  SalesQuote,
  SalesQuoteDetailInput,
  UpdateSalesQuoteInput,
} from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { AppRoutes } from "store/routes"
import { normalizeNumberInput } from "utils/numberInputHelpers"

const SalesQuoteDetail = () => {
  const { quoteNbr } = useParams<{ quoteNbr: string }>()
  const navigate = useNavigate()
  const {
    salesQuotes,
    // salesQuoteDetails
  } = useStoreState((state) => state)
  const {
    fetchSalesQuotes,
    // getSalesQuoteByQuoteNbrWithDetails,
    updateSalesQuote,
    // getSalesQuoteDetails,
  } = useStoreActions((actions) => actions)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [quote, setQuote] = useState<SalesQuote | null>(null)
  const [editing, setEditing] = useState(false)
  // const [isNewlyCreated, setIsNewlyCreated] = useState(false)
  // const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Form fields
  const [formData, setFormData] = useState({
    businessAccount: "",
    currency: "",
    date: "",
    description: "",
    status: "",
    textHeader: "",
    textFooter: "",
  })

  const [details, setDetails] = useState<SalesQuoteDetailInput[]>([])

  // fetch fresh data
  useEffect(() => {
    let isActive = true

    const loadSalesQuotes = async () => {
      try {
        if (!isActive) return
        setLoading(true)
        setError(null)
        await fetchSalesQuotes({ fresh: true })
      } catch (err) {
        console.error("Error loading sales quote details:", err)
        if (isActive) {
          setError("Fehler beim Laden der Angebote. Bitte versuchen Sie es erneut.")
        }
      } finally {
        if (isActive) {
          setLoading(false)
        }
      }
    }

    loadSalesQuotes()

    return () => {
      isActive = false
    }
  }, [fetchSalesQuotes])

  //set quote by comparing quoteNbr with salesQuotes
  useEffect(() => {
    const quote = salesQuotes.find((q) => q.quoteNbr === quoteNbr)

    if (quote) {
      setQuote(quote)
      // Initialize form data when quote is loaded
      setFormData({
        businessAccount: quote.businessAccount || "",
        currency: quote.currency || "",
        date: quote.date || "",
        description: quote.description || "",
        status: quote.status || "",
        textHeader: quote.textHeader || "",
        textFooter: quote.textFooter || "",
      })
    }
  }, [salesQuotes, quoteNbr])

  const handleSave = async () => {
    if (!quote) return

    // Ensure we have a quoteNbr since the backend requires it for updates
    if (!quote.quoteNbr) {
      setError("Cannot update quote: Quote Number is missing.")
      return
    }

    try {
      setSaving(true)
      const input: UpdateSalesQuoteInput = {
        id: quote.id,
        quoteNbr: quote.quoteNbr, // Required for updates
        businessAccount: formData.businessAccount || undefined,
        currency: formData.currency || undefined,
        date: formData.date || undefined,
        description: formData.description || undefined,
        status: formData.status || undefined,
        textHeader: formData.textHeader || undefined,
        textFooter: formData.textFooter || undefined,
        details: details.length > 0 ? details : undefined,
      }

      await updateSalesQuote(input)
      setEditing(false)
      setError(null) // Clear any previous errors
      // Optionally show success message
    } catch (err) {
      console.error("Error updating quote:", err)
      setError("Failed to update quote. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  const handleEditToggle = () => {
    setEditing(!editing)
    if (editing) {
      // Reset form data when canceling edit
      if (quote) {
        setFormData({
          businessAccount: quote.businessAccount || "",
          currency: quote.currency || "",
          date: quote.date || "",
          description: quote.description || "",
          status: quote.status || "",
          textHeader: quote.textHeader || "",
          textFooter: quote.textFooter || "",
        })
      }
    }
  }

  const handleFormChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleDetailChange = (
    index: number,
    field: keyof SalesQuoteDetailInput,
    value: string | number | boolean
  ) => {
    const updatedDetails = [...details]
    if (
      field === "quantity" ||
      field === "unitPrice" ||
      field === "discount" ||
      field === "discountAmount"
    ) {
      // For number fields, handle both string (during editing) and number (final value)
      updatedDetails[index] = {
        ...updatedDetails[index],
        [field]:
          typeof value === "string" ? normalizeNumberInput(value) : value,
      }
    } else {
      updatedDetails[index] = {
        ...updatedDetails[index],
        [field]: value,
      }
    }
    setDetails(updatedDetails)
  }

  const addDetail = () => {
    setDetails((prev) => [
      ...prev,
      {
        inventoryID: "",
        quantity: 1,
        unitPrice: 0,
        discount: 0,
        discountAmount: 0,
        uom: "STÜCK",
      },
    ])
  }

  const removeDetail = (index: number) => {
    setDetails((prev) => prev.filter((_, i) => i !== index))
  }

  // const handleRefresh = async () => {
  //   // Increment refresh trigger to cause useEffect to re-run with fresh data
  //   setRefreshTrigger((prev) => prev + 1)
  // }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !quote) {
    return (
      <Box sx={{ p: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(AppRoutes.SALES_QUOTES)}
          sx={{ mb: 2 }}
        >
          Back to Sales Quotes
        </Button>
        <Typography color="error" variant="h6">
          {error || "Quote not found"}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(AppRoutes.SALES_QUOTES)}
        sx={{ mb: 2 }}
      >
        Back to Sales Quotes
      </Button>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h4">
              Sales Quote {quote.quoteNbr || quoteNbr}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {/* {isNewlyCreated && (
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={loading}
                  size="small"
                >
                  Refresh
                </Button>
              )} */}
              <Button
                variant="contained"
                color={editing ? "primary" : "secondary"}
                startIcon={editing ? <Save /> : <Edit />}
                onClick={editing ? handleSave : handleEditToggle}
                disabled={true}
              >
                {saving ? (
                  <CircularProgress size={24} />
                ) : editing ? (
                  "Änderungen speichern"
                ) : (
                  "Angebot bearbeiten"
                )}
              </Button>
            </Box>
          </Box>

          {/* {isNewlyCreated && !quote.quoteNbr && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This quote was just created and may still be processing. Some
              fields might be empty until the system finishes setting up the
              quote.{" "}
              <Button
                size="small"
                onClick={handleRefresh}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                Click here to refresh
              </Button>
            </Alert>
          )} */}

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={quote.status || "New"}
                    color={quote.status === "Approved" ? "success" : "primary"}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography>{quote.businessAccount || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Opportunity
                  </Typography>
                  <Typography>{quote.opportunityID || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total
                  </Typography>
                  <Typography>
                    {quote.total ? `${quote.total.toFixed(2)}` : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography>
                    {quote.date
                      ? new Date(quote.date).toLocaleDateString()
                      : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Expiration
                  </Typography>
                  <Typography>
                    {quote.expirationDate
                      ? new Date(quote.expirationDate).toLocaleDateString()
                      : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Contact
                  </Typography>
                  <Typography>{quote.contactContact || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography noWrap>{quote.description || "—"}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Header & Footer Text Editing */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Header & Footer Text
              </Typography>

              {editing ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Business Account"
                      fullWidth
                      value={formData.businessAccount}
                      onChange={(e) =>
                        handleFormChange("businessAccount", e.target.value)
                      }
                      variant="outlined"
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Währung"
                      fullWidth
                      value={formData.currency}
                      onChange={(e) =>
                        handleFormChange("currency", e.target.value)
                      }
                      variant="outlined"
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Date"
                      type="date"
                      fullWidth
                      value={formData.date ? formData.date.split("T")[0] : ""}
                      onChange={(e) => handleFormChange("date", e.target.value)}
                      variant="outlined"
                      margin="normal"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Status"
                      fullWidth
                      value={formData.status}
                      onChange={(e) =>
                        handleFormChange("status", e.target.value)
                      }
                      variant="outlined"
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Description"
                      fullWidth
                      multiline
                      rows={2}
                      value={formData.description}
                      onChange={(e) =>
                        handleFormChange("description", e.target.value)
                      }
                      variant="outlined"
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Header Text (HTML)"
                      multiline
                      rows={4}
                      fullWidth
                      value={formData.textHeader}
                      onChange={(e) =>
                        handleFormChange("textHeader", e.target.value)
                      }
                      variant="outlined"
                      margin="normal"
                      sx={{
                        "& .MuiInputBase-input": {
                          color: "black",
                        },
                        "& .MuiInputBase-root": {
                          color: "black",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Footer Text (HTML)"
                      multiline
                      rows={4}
                      fullWidth
                      value={formData.textFooter}
                      onChange={(e) =>
                        handleFormChange("textFooter", e.target.value)
                      }
                      variant="outlined"
                      margin="normal"
                      sx={{
                        "& .MuiInputBase-input": {
                          color: "black",
                        },
                        "& .MuiInputBase-root": {
                          color: "black",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Header Text
                    </Typography>
                    {quote.textHeader ? (
                      <Paper
                        sx={{ p: 2, backgroundColor: "#f5f5f5" }}
                        variant="outlined"
                      >
                        <div
                          dangerouslySetInnerHTML={{ __html: quote.textHeader }}
                          style={{ color: "black" }}
                        />
                      </Paper>
                    ) : (
                      <Typography color="text.secondary" fontStyle="italic">
                        No header text set
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Footer Text
                    </Typography>
                    {quote.textFooter ? (
                      <Paper
                        sx={{ p: 2, backgroundColor: "#f5f5f5" }}
                        variant="outlined"
                      >
                        <div
                          dangerouslySetInnerHTML={{ __html: quote.textFooter }}
                          style={{ color: "black" }}
                        />
                      </Paper>
                    ) : (
                      <Typography color="text.secondary" fontStyle="italic">
                        No footer text set
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>

          {/* Products/Items */}
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Products</Typography>
                {editing && (
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addDetail}
                    size="small"
                  >
                    Add Product
                  </Button>
                )}
              </Box>

              {details.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Line #</TableCell>
                        <TableCell>Product ID</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Discount %</TableCell>
                        <TableCell align="right">Discount Amount</TableCell>
                        <TableCell align="right">UOM</TableCell>
                        {editing && (
                          <TableCell align="right">Actions</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {details.map((detail, index) => (
                        <TableRow key={detail.id || index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            {editing ? (
                              <TextField
                                size="small"
                                value={detail.inventoryID || ""}
                                onChange={(e) =>
                                  handleDetailChange(
                                    index,
                                    "inventoryID",
                                    e.target.value
                                  )
                                }
                                variant="outlined"
                              />
                            ) : (
                              detail.inventoryID || "—"
                            )}
                          </TableCell>
                          <TableCell>
                            Product {detail.inventoryID || index + 1}
                          </TableCell>
                          <TableCell align="right">
                            {editing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={detail.quantity || ""}
                                onChange={(e) =>
                                  handleDetailChange(
                                    index,
                                    "quantity",
                                    e.target.value
                                  )
                                }
                                onBlur={(e) => {
                                  const normalizedValue = normalizeNumberInput(
                                    e.target.value
                                  )
                                  if (normalizedValue !== detail.quantity) {
                                    handleDetailChange(
                                      index,
                                      "quantity",
                                      normalizedValue
                                    )
                                  }
                                }}
                                variant="outlined"
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            ) : (
                              detail.quantity || "—"
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {editing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={detail.unitPrice || ""}
                                onChange={(e) =>
                                  handleDetailChange(
                                    index,
                                    "unitPrice",
                                    e.target.value
                                  )
                                }
                                onBlur={(e) => {
                                  const normalizedValue = normalizeNumberInput(
                                    e.target.value
                                  )
                                  if (normalizedValue !== detail.unitPrice) {
                                    handleDetailChange(
                                      index,
                                      "unitPrice",
                                      normalizedValue
                                    )
                                  }
                                }}
                                variant="outlined"
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            ) : detail.unitPrice ? (
                              detail.unitPrice.toFixed(2)
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {editing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={detail.discount || ""}
                                onChange={(e) =>
                                  handleDetailChange(
                                    index,
                                    "discount",
                                    e.target.value
                                  )
                                }
                                onBlur={(e) => {
                                  const normalizedValue = normalizeNumberInput(
                                    e.target.value
                                  )
                                  if (normalizedValue !== detail.discount) {
                                    handleDetailChange(
                                      index,
                                      "discount",
                                      normalizedValue
                                    )
                                  }
                                }}
                                variant="outlined"
                                inputProps={{ min: 0, max: 100, step: 0.01 }}
                              />
                            ) : detail.discount ? (
                              detail.discount.toFixed(2) + "%"
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {editing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={detail.discountAmount || ""}
                                onChange={(e) =>
                                  handleDetailChange(
                                    index,
                                    "discountAmount",
                                    e.target.value
                                  )
                                }
                                onBlur={(e) => {
                                  const normalizedValue = normalizeNumberInput(
                                    e.target.value
                                  )
                                  if (
                                    normalizedValue !== detail.discountAmount
                                  ) {
                                    handleDetailChange(
                                      index,
                                      "discountAmount",
                                      normalizedValue
                                    )
                                  }
                                }}
                                variant="outlined"
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            ) : detail.discountAmount ? (
                              detail.discountAmount.toFixed(2)
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {editing ? (
                              <TextField
                                size="small"
                                value={detail.uom || "STÜCK"}
                                onChange={(e) =>
                                  handleDetailChange(
                                    index,
                                    "uom",
                                    e.target.value
                                  )
                                }
                                variant="outlined"
                              />
                            ) : (
                              detail.uom || "STÜCK"
                            )}
                          </TableCell>
                          {editing && (
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => removeDetail(index)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary" fontStyle="italic">
                  No products found for this quote
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default SalesQuoteDetail
